import { useState } from 'react';
import type { ReactNode } from 'react';
import { AuthError } from '../auth/provider';
import type { AuthProvider } from '../auth/provider';
import { Rich } from './bits';
import { useT } from '../messages';

/**
 * Credential prefill from the URL FRAGMENT — a deliberate per-app copy of the
 * staff PWA's (`apps/app/src/ui/SignIn.tsx`), for `format.ts`' reason: apps do
 * not cross-import, and thirty lines shared between two sign-in screens would
 * exist only to couple them. The argument lives with the original: fragment,
 * never query (a query string persists a password in the LB's request logs);
 * read once; address bar scrubbed immediately; never auto-submitted.
 */
function readFragmentPrefill(): { email: string; password: string } {
  try {
    const raw = window.location.hash;
    if (!raw.includes('email=')) return { email: '', password: '' };
    const params = new URLSearchParams(raw.replace(/^#/, ''));
    const email = params.get('email') ?? '';
    const password = params.get('password') ?? '';
    if (email !== '' || password !== '') {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    return { email, password };
  } catch {
    return { email: '', password: '' };
  }
}

/**
 * Sign in.
 *
 * The dev banner is not decoration, and it matters more here than on the staff
 * app. `VITE_AUTH_FAKE` mints unsigned tokens, and `admin-api`'s `FakeVerifier`
 * refuses to exist under `NODE_ENV=production` with a sentence that says why:
 * an unsigned operator token is not one tenant's book, it is every tenant's. A
 * screen that looked identical in both modes would undo that on this side of
 * the wire.
 *
 * There is no "create account" and there will not be one. The operator claim is
 * set by an operator provisioning another operator; a staff token from a real
 * tenant is refused at the door, and so is a token that carries both claims.
 */
export function SignIn({ auth }: { auth: AuthProvider }): ReactNode {
  const t = useT();
  const [prefill] = useState(readFragmentPrefill);
  const [email, setEmail] = useState(prefill.email);
  const [password, setPassword] = useState(prefill.password);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await auth.signIn(email, password);
    } catch (e) {
      setError(
        e instanceof AuthError && e.reason === 'unavailable'
          ? t('signin.unavailable')
          : t('signin.rejected'),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="signin">
      <form
        className="signin__panel"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <h1>{t('signin.title')}</h1>
        <p className="signin__sub">
          <Rich text={t('signin.sub')} />
        </p>

        {auth.kind === 'fake' ? (
          <p className="signin__dev">
            <Rich text={t('signin.dev')} />
          </p>
        ) : null}

        <label className="field">
          <span className="field__label">{t('signin.email')}</span>
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>

        {auth.kind === 'firebase' ? (
          <label className="field">
            <span className="field__label">{t('signin.password')}</span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
        ) : null}

        {error !== null ? <p className="failure__message">{error}</p> : null}

        <button type="submit" className="button button--primary" disabled={busy}>
          {busy ? t('signin.busy') : t('signin.submit')}
        </button>
      </form>
    </main>
  );
}
